import axios from 'axios';
import { handleError, parseAxiosError, AppError, ErrorCode } from '../utils/errorHandler.js';
import { apiClientWithRetry } from '../shared/services/apiClient.js';
import { getFullApiUrl } from '../shared/utils/apiConfig';

// Get tenant-specific API URL using centralized configuration
export const getTenantApiUrl = (): string => {
  return getFullApiUrl();
};

// Base URL for API requests with tenant-aware routing (evaluated per-call to avoid stale values)
const getApiBaseUrl = () => getTenantApiUrl();

// Get tenant context for API calls
export const getTenantContext = (): string | null => {
  const user = localStorage.getItem('user');
  if (user) {
    try {
      const userData = JSON.parse(user);
      return userData.tenant;
    } catch (e) {
      console.warn('Failed to parse user data from localStorage');
    }
  }
  return null;
};

// Configure axios defaults for tenant-aware requests
export const configureAxiosDefaults = (): void => {
  const tenant = getTenantContext();
  if (tenant) {
    axios.defaults.headers.common['X-Tenant-ID'] = tenant;
  }
};

// Initialize tenant context
configureAxiosDefaults();

// Alert interfaces
export interface Alert {
  id: string;
  title: string;
  description: string;
  severity: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  source: string;
  tenant_id: string;
  [key: string]: any;
}

export interface AlertsResponse {
  alerts: Alert[];
  total: number;
  page: number;
  pageSize: number;
  metadata?: {
    critical_count?: number;
    enriched_count?: number;
    [key: string]: any;
  };
}

export interface AlertStatusUpdate {
  status: string;
  metadata?: Record<string, any>;
  updatedAt?: string;
}

export interface Recommendation {
  id: string;
  alertId: string;
  description: string;
  action: string;
  confidence: number;
  createdAt: string;
  [key: string]: any;
}

/**
 * Alert filter parameters (Dec 7, 2025)
 * Following 00-READ-THIS-FIRST: Explicit types, no assumptions
 */
export interface AlertFilterParams {
  page?: number;
  page_size?: number;
  severity?: string;
  status?: string;
  source?: string;
  show_all?: boolean;  // Show all alerts including pending enrichment (default: false)
  include_auto_dismissed?: boolean;  // Include alerts with ML confidence < 0.7 (default: false)
  [key: string]: any;
}

/**
 * Fetch alerts with optional filtering parameters
 * @param params - Query parameters for filtering alerts
 * @returns Promise resolving to alerts data
 */
export const fetchAlerts = async (params: AlertFilterParams = {}): Promise<AlertsResponse> => {
  try {
    const response = await apiClientWithRetry.get<AlertsResponse>(`${getApiBaseUrl()}/alerts`, { params });
    
    // Validate response structure
    if (!response.data) {
      throw new Error('API returned undefined data');
    }
    
    if (!response.data.alerts) {
      throw new Error('API response missing alerts array');
    }
    
    return response.data;
  } catch (error) {
    console.error('❌ [ERROR] fetchAlerts failed:', error);
    
    // Use our standardized error handling
    const appError = handleError(error as AppError, (err) => {
      // You can add specific error handling logic here
      if (err.code === ErrorCode.TENANT_NOT_FOUND) {
        // Handle tenant not found specifically
        console.warn('Tenant context not found when fetching alerts');
      }
    });
    
    throw appError;
  }
};

/**
 * Fetch details for a specific alert
 * @param alertId - ID of the alert to fetch
 * @returns Promise resolving to alert details
 */
export const fetchAlertDetails = async (alertId: string): Promise<Alert> => {
  try {
    const response = await apiClientWithRetry.get<Alert>(`${getApiBaseUrl()}/alerts/${alertId}`);
    return response.data;
  } catch (error) {
    const appError = handleError(error as AppError);
    
    // Add context to the error
    appError.details = {
      ...appError.details,
      alertId,
      operation: 'fetchAlertDetails'
    };
    
    throw appError;
  }
};

/**
 * Update the status of an alert
 * @param alertId - ID of the alert to update
 * @param status - New status for the alert
 * @param metadata - Additional metadata for the status update
 * @returns Promise resolving to updated alert data
 */
export const updateAlertStatus = async (
  alertId: string, 
  status: string, 
  metadata: Record<string, any> = {}
): Promise<Alert> => {
  try {
    const payload: AlertStatusUpdate = {
      status,
      metadata,
      updatedAt: new Date().toISOString()
    };
    
    const response = await apiClientWithRetry.patch<Alert>(
      `${getApiBaseUrl()}/alerts/${alertId}/status`, 
      payload
    );
    
    return response.data;
  } catch (error) {
    const appError = handleError(error as AppError);
    
    // Add context to the error
    appError.details = {
      ...appError.details,
      alertId,
      status,
      operation: 'updateAlertStatus'
    };
    
    throw appError;
  }
};

/**
 * Fetch recommendations for a specific alert
 * @param alertId - ID of the alert to get recommendations for
 * @returns Promise resolving to recommendations data
 */
export const fetchRecommendations = async (alertId: string): Promise<Recommendation[]> => {
  try {
    const response = await apiClientWithRetry.get<Recommendation[]>(
      `${getApiBaseUrl()}/alerts/${alertId}/recommendations`
    );
    return response.data;
  } catch (error) {
    const appError = handleError(error as AppError);
    
    // Add context to the error
    appError.details = {
      ...appError.details,
      alertId,
      operation: 'fetchRecommendations'
    };
    
    throw appError;
  }
};

/**
 * Acknowledge an alert
 * @param alertId - ID of the alert to acknowledge
 * @returns Promise resolving to updated alert data
 */
export const acknowledgeAlert = async (alertId: string): Promise<Alert> => {
  try {
    const response = await apiClientWithRetry.post<Alert>(
      `${getApiBaseUrl()}/alerts/${alertId}/acknowledge`,
      {}
    );
    return response.data;
  } catch (error) {
    const appError = handleError(error as AppError);
    
    // Add context to the error
    appError.details = {
      ...appError.details,
      alertId,
      operation: 'acknowledgeAlert'
    };
    
    throw appError;
  }
};

/**
 * Resolve an alert
 * @param alertId - ID of the alert to resolve
 * @param resolutionNotes - Notes about how the alert was resolved
 * @returns Promise resolving to updated alert data
 */
export const resolveAlert = async (
  alertId: string, 
  resolutionNotes: string = ''
): Promise<Alert> => {
  try {
    const response = await apiClientWithRetry.post<Alert>(
      `${getApiBaseUrl()}/alerts/${alertId}/resolve`,
      { resolutionNotes }
    );
    return response.data;
  } catch (error) {
    const appError = handleError(error as AppError);
    
    // Add context to the error
    appError.details = {
      ...appError.details,
      alertId,
      operation: 'resolveAlert'
    };
    
    throw appError;
  }
};
